import { NvButton, NvModal } from '@simwood/nv-react-components';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DeleteIcon, EditIcon, PlusIcon } from '../../../app/components/icons';
import { deletePhonebookEntry } from '../../reducers/contacts';
import {
  AugmentedPhoneExtension,
  getContactNumber,
} from '../../services/contactsService';
import { StoreDispatch, StoreState } from '../../store';
import { ApiPhonebookEntry } from '../../types/PhonebookEntry';
import { ApiVirtualExtension } from '../../types/VirtualExtension';
import Contact, { Props as ContactProps } from '../Contact';
import ContactEditModal from '../ContactEditModal';
import Spinner from '../Spinner';
import * as styles from './styles.module.scss';

interface Props {
  disabled?: boolean;
  compact?: boolean;
  showAddButton: boolean;
  contacts: (
    | ApiPhonebookEntry
    | AugmentedPhoneExtension
    | ApiVirtualExtension
  )[];
  onContactClick: (
    contact: ApiPhonebookEntry | AugmentedPhoneExtension | ApiVirtualExtension,
  ) => void;
}

const ContactPane: React.FC<Props> = ({
  disabled,
  compact,
  showAddButton,
  contacts,
  onContactClick,
}) => {
  const dispatch = useDispatch<StoreDispatch>();
  const [editingContactId, setEditingContactId] = useState('');
  const [deletingContactId, setDeletingContactId] = useState('');
  const isFetching = useSelector(
    (state: StoreState) => state.contacts.isFetching,
  );
  const deletingContact = useMemo(
    () => contacts.find((c) => c.id === deletingContactId),
    [contacts, deletingContactId],
  );
  const extraActions: ContactProps['extraActions'] = useMemo(
    () => [
      {
        id: 'edit',
        name: 'Edit',
        icon: <EditIcon />,
        onClick: (id: string) => {
          setEditingContactId(id);
        },
      },
      {
        id: 'delete',
        name: 'Delete',
        icon: <DeleteIcon />,
        kind: 'danger',
        onClick: async (id: string) => {
          setDeletingContactId(id);
        },
      },
    ],
    [],
  );

  const classes = [styles.list, compact ? styles.compact : ''].join(' ').trim();

  if (!contacts) {
    return <span>Loading contacts</span>;
  }

  return (
    <>
      <ul className={classes}>
        {showAddButton && (
          <li className={`${styles.itemContainer} ${styles.buttonContainer}`}>
            <NvButton
              leftIcon={<PlusIcon />}
              onClick={() => {
                setEditingContactId('0');
              }}
            >
              Add
            </NvButton>
          </li>
        )}
        {contacts.map((contact) => {
          const number = getContactNumber(contact);

          return (
            <Contact
              key={contact.id}
              compact={compact}
              disabled={disabled}
              name={contact.name}
              description={number}
              email={
                (contact as ApiPhonebookEntry | AugmentedPhoneExtension).email
              }
              id={contact.id}
              type={contact.type}
              status={(contact as AugmentedPhoneExtension).status}
              extraActions={
                contact.type === 'phonebookentry' ? extraActions : []
              }
              onClick={() => onContactClick(contact)}
            />
          );
        })}
        {isFetching ? (
          <li className={styles.itemContainer}>
            <Spinner text='Loading' />
          </li>
        ) : (
          ''
        )}
      </ul>
      {editingContactId && (
        <ContactEditModal
          fullScreen={compact}
          contactId={editingContactId === '0' ? undefined : editingContactId}
          onClose={() => {
            setEditingContactId('');
          }}
        />
      )}

      <NvModal.Confirm
        // TODO this was here
        // fullScreen={compact}
        open={!!deletingContactId}
        setOpen={(open) => {
          if (!open) {
            setDeletingContactId('');
          }
        }}
        kind='danger'
        submitText='Delete'
        title='Confirm deleting'
        onSubmit={() => {
          dispatch(deletePhonebookEntry(deletingContactId));
        }}
      >
        Are you sure you want to remove phonebook entry for{' '}
        {deletingContact?.name}?
      </NvModal.Confirm>
    </>
  );
};

export default ContactPane;
